import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";
import classNames from "classnames";

import { ReferralStatsCard, ReferralStatsProgressBar } from "./components";
import type { UseUserReferralData } from "./hooks";
import { useUserReferralData } from "./hooks";
import type { LoyaltyReferralStatsProps } from "./LoyaltyReferralStats.types";

import styles from "./LoyaltyReferralStats.module.scss";

const themes = {
  light: "light-theme",
  dark: "dark-theme",
};

const EDITOR_DATA: ReturnType<UseUserReferralData> = {
  isLoading: false,
  isAllAchievementsDone: false,
  totalUserReferrals: 5,
  referredFriendsCountForStatistics: 5,
  friendsReferralsUntilNextAchievement: 5,
  milestones: [
    {
      mileStonePointsValue: 100,
      referralNumber: 2,
    },
    {
      mileStonePointsValue: 200,
      referralNumber: 5,
    },
    {
      mileStonePointsValue: 400,
      referralNumber: 10,
    },
    {
      mileStonePointsValue: 600,
      referralNumber: 15,
    },
  ],
  pointsEarned: 300,
  isComponentHidden: false,
};

export const LoyaltyReferralStats = ({
  heading,
  subHeading,
  friendsReferredDescription,
  friendsReferredInfoHeading,
  friendsReferredPoints,
  friendsReferredSubText,
  pointsEarnedDescription,
  pointsEarnedCtaLink,
  pointsEarnedCtaText,
  nextTierInfoDescription,
  nextTierInfoCtaLink,
  nextTierInfoCtaText,
  progressBlockBg,
  progressBlockBgTablet,
  progressBlockBgMobile,
  progressBlockHeading,
  progressBlockDescription,
  progressBlockHeadingAllDone,
  progressBlockDescriptionAllDone,
  progressBarText,
  milestonesText,
  achievementsDoneHeading,
  achievementsDoneText,
  isInEditor,
  theme,
  isStatsVisible,
  isAnimatedNumberEnabled = false,
}: LoyaltyReferralStatsProps) => {
  const { mainBreakpoint } = useCurrentBreakpoint();

  const userReferralData = useUserReferralData();

  const {
    isLoading,
    friendsReferralsUntilNextAchievement,
    isAllAchievementsDone,
    totalUserReferrals,
    referredFriendsCountForStatistics,
    pointsEarned,
    milestones,
    isComponentHidden,
  } = isInEditor ? EDITOR_DATA : userReferralData;

  const themeClassName = theme ? themes[theme] : undefined;
  const isMobileOrTablet = ["xs", "sm"].includes(mainBreakpoint);
  const getBgImage = () => {
    if (mainBreakpoint === "xs") {
      return progressBlockBgMobile;
    }
    if (mainBreakpoint === "sm") {
      return progressBlockBgTablet;
    }
    return progressBlockBg;
  };

  const [
    untilNextAchievementHeading,
    untilNextAchievementText,
    progressBlockHeadingText,
    progressBlockDescriptionText,
  ] = isAllAchievementsDone
    ? [achievementsDoneHeading, achievementsDoneText, progressBlockHeadingAllDone, progressBlockDescriptionAllDone]
    : [friendsReferralsUntilNextAchievement, nextTierInfoDescription, progressBlockHeading, progressBlockDescription];

  if (!isInEditor && (isLoading || isComponentHidden)) {
    return null;
  }

  return (
    <div className={classNames(styles.wrapper, themeClassName)} data-testid="loyaltyReferralStats">
      <h2 className={styles.heading}>{heading}</h2>
      <h3 className={styles.subHeading}>{subHeading}</h3>
      <div className={styles.statsWrapper}>
        <ReferralStatsCard
          count={String(referredFriendsCountForStatistics)}
          badgeText={friendsReferredPoints}
          additionalText={friendsReferredSubText}
          headerText={friendsReferredDescription}
          informationText={friendsReferredInfoHeading}
        />

        <ReferralStatsCard
          isRedirect={true}
          count={String(pointsEarned)}
          href={pointsEarnedCtaLink}
          headerText={pointsEarnedDescription}
          ctaButtonText={pointsEarnedCtaText}
          isAnimatedNumberEnabled={isAnimatedNumberEnabled}
        />

        {isStatsVisible && (
          <ReferralStatsCard
            isRedirect={true}
            isReferralCTA={true}
            href={nextTierInfoCtaLink}
            count={String(untilNextAchievementHeading)}
            headerText={untilNextAchievementText}
            ctaButtonText={nextTierInfoCtaText}
            isAllAchievementsDone={false}
          />
        )}
      </div>
      {isStatsVisible && (
        <div
          className={classNames(styles.statsProgressWrapper, "dark-theme")}
          style={{ "--referral-stats-bg": `url(${getBgImage()})` }}
        >
          <div className={styles.statsProgressText}>
            <h2 className={styles.statsProgressHeading}>{progressBlockHeadingText}</h2>
            <p className={styles.statsProgressDescription}>{progressBlockDescriptionText}</p>
          </div>
          <div className={classNames(styles.statsProgressBar, "light-theme")}>
            <ReferralStatsProgressBar
              milestones={milestones}
              userCurrentReferralNumber={totalUserReferrals}
              progressBarText={progressBarText}
              milestonesText={milestonesText}
              isMobileOrTablet={isMobileOrTablet}
            />
          </div>
        </div>
      )}
    </div>
  );
};
